import { mimeTypes } from 'app/utils/constants'

/** Transform the blob object into a file object. */
export const blobToFile = (
    blob: Blob,
    link: string,
    options?: FilePropertyBag,
): File => {
    const linkSplitted = link.split('/')
    const nameSplitted = linkSplitted.at(-1).split('.')

    const [name] = nameSplitted
    const type = nameSplitted[1].includes('pdf') ? mimeTypes.PDF : mimeTypes.PNG

    const nameWithExtension =
        type === mimeTypes.PDF ? `${name}.pdf` : `${name}.png`

    return new File([blob], nameWithExtension, {
        ...options,
        type,
    })
}

/** Transform the data into form data to send to the backend. */
export const dataToFormData = (
    data: any,
    prefix = '',
    formData = new FormData(),
): FormData => {
    const isObject =
        data instanceof Object &&
        !Array.isArray(data) &&
        !(data instanceof File)

    const isArray = Array.isArray(data)

    if (isObject) {
        for (const [key, value] of Object.entries(data)) {
            const isValueObject =
                value instanceof Object &&
                !Array.isArray(value) &&
                !(value instanceof File)

            const isValueArray = Array.isArray(value)
            const finalKey = prefix ? `${prefix}[${key}]` : key

            if (isValueObject || isValueArray) {
                dataToFormData(value, finalKey, formData)
            } else if (value !== undefined) {
                formData.append(finalKey, value as any)
            }
        }
    }

    if (isArray) {
        for (const [index, value] of data.entries()) {
            const isValueObject =
                value instanceof Object &&
                !Array.isArray(value) &&
                !(value instanceof File)

            const isValueArray = Array.isArray(value)
            const finalKey = `${prefix}[${index}]`

            if (isValueObject || isValueArray) {
                dataToFormData(value, finalKey, formData)
            } else if (value !== undefined) {
                formData.append(finalKey, value as any)
            }
        }
    }

    return formData
}

// Keys that should be arrays in the result
const ARRAY_KEYS = new Set(['allergens'])

// Keys that must be preserved as strings (even if numeric-looking)
const STRING_KEYS = new Set(['ean'])

// Extract the last segment from bracket paths: "products[0][ean]" -> "ean"
const lastSegment = (key: string) =>
    key
        .split(/\]\[|\[|\]/u)
        .reverse()
        .find(Boolean)

// --- coercers now aware of the field key ---
const coercePrimitive = (stringParameter: string, key: string) => {
    const stringValue = stringParameter.trim()

    // Preserve-whitelist: never number-coerce these
    if (STRING_KEYS.has(lastSegment(key))) return stringValue

    if (stringValue === 'true') return true
    if (stringValue === 'false') return false
    if (stringValue === 'null') return null
    if (stringValue !== '' && !Number.isNaN(Number(stringValue)))
        return Number(stringValue)
    return stringValue
}

const coerceValue = (value: FormDataEntryValue, key: string): any => {
    if (value instanceof File) return value
    return coercePrimitive(value.toString(), key)
}

/** Reverse form data to readable data with proper types. */
export const formDataToData = (formData: FormData) => {
    const result: any = {}

    for (const [key, raw] of formData.entries()) {
        const value = coerceValue(raw, key)

        // Simple key (no bracket notation)
        if (!key.includes('[')) {
            if (result[key] === undefined) {
                result[key] = value
            } else if (Array.isArray(result[key])) {
                result[key].push(value)
            } else {
                result[key] = [result[key], value]
            }

            // eslint-disable-next-line no-continue
            continue
        }

        // Nested key like "foo[0][bar]"
        const parts = key.split(/\]\[|\[|\]/u).filter(Boolean)
        let cursor: any = result

        for (let index = 0; index < parts.length - 1; index++) {
            const part = parts[index]

            // Initialize when null OR undefined
            if (cursor[part] === null || cursor[part] === undefined) {
                const nextIsIndex = !Number.isNaN(Number(parts[index + 1]))
                cursor[part] = nextIsIndex ? [] : {}
            }
            cursor = cursor[part]
        }

        const last = parts.at(-1)
        cursor[last] = value
    }

    // Post-normalization for known array keys
    for (const key of ARRAY_KEYS) {
        // If this key never appeared in the formData, skip it.
        if (!(key in result)) {
            // eslint-disable-next-line no-continue
            continue
        }

        const value = result[key]

        if (value === undefined || value === '') {
            result[key] = []
        } else if (typeof value === 'string') {
            result[key] = value
                .split(',')
                .map((string) => string.trim())
                .filter((string) => string !== '')
                .map((element) => coercePrimitive(element, key))
        } else if (!Array.isArray(value)) {
            result[key] = [value]
        }
    }

    return result
}
